/**
 * NotificationToast Component
 *
 * Renders the Redux ui.notifications array as a stack of toast notifications.
 * Place this once at the app root (App.tsx) so it overlays every page/modal.
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { removeNotification, selectNotifications } from '../../../store/slices/uiSlice';
import type { Notification } from '../../../store/slices/uiSlice';

const ICON_MAP = {
  success: <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />,
  error: <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" />,
  warning: <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0" />,
  info: <Info className="h-5 w-5 text-blue-400 flex-shrink-0" />,
};

const BG_MAP = {
  success: 'bg-gray-900 dark:bg-gray-800 border-green-500/40',
  error: 'bg-gray-900 dark:bg-gray-800 border-red-500/40',
  warning: 'bg-gray-900 dark:bg-gray-800 border-amber-500/40',
  info: 'bg-gray-900 dark:bg-gray-800 border-blue-500/40',
};

const TITLE_MAP = {
  success: 'text-green-300',
  error: 'text-red-300',
  warning: 'text-amber-300',
  info: 'text-blue-300',
};

interface ToastItemProps {
  notification: Notification;
  onDismiss: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ notification, onDismiss }) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (notification.duration && notification.duration > 0) {
      timerRef.current = setTimeout(() => {
        onDismiss(notification.id);
      }, notification.duration);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [notification.id, notification.duration, onDismiss]);

  return (
    <div
      className={`pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border p-4 shadow-lg ${BG_MAP[notification.type]} animate-slide-up`}
      role="alert"
    >
      {/* Icon */}
      <div className="mt-0.5">{ICON_MAP[notification.type]}</div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {notification.title && (
          <p className={`text-sm font-semibold ${TITLE_MAP[notification.type]}`}>
            {notification.title}
          </p>
        )}
        <p className="text-sm text-gray-300 mt-0.5 break-words">{notification.message}</p>
      </div>

      {/* Close button */}
      <button
        onClick={() => onDismiss(notification.id)}
        className="flex-shrink-0 rounded p-0.5 text-gray-500 hover:text-gray-200 transition-colors"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export const NotificationToast: React.FC = () => {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(selectNotifications);

  const handleDismiss = (id: string) => {
    dispatch(removeNotification(id));
  };

  if (notifications.length === 0) return null;

  return createPortal(
    <div
      className="pointer-events-none fixed bottom-6 right-6 z-[70] flex flex-col gap-2 w-full max-w-sm"
      aria-live="polite"
      aria-atomic="false"
    >
      {notifications.map((n) => (
        <ToastItem key={n.id} notification={n} onDismiss={handleDismiss} />
      ))}
    </div>,
    document.body
  );
};
